/**
 * AppShell — headless outer-shell router.
 *
 * Headless: no `browser.*`, no `chrome.*`, no storage hooks, no IPC
 * dispatch. All data arrives as props; the host resolves storage
 * reads (`paired` / `pairingJustConfirmed`), passes them in, and
 * receives a callback when the main view becomes active so it can
 * fire its own platform-side side effects (MARK_READ in the extension,
 * no-op in the web today). Mirrors the conventions established by
 * the other headless feature wrappers in this package
 * (`SettingsPanel`, `HeaderBar`, `SidePanelNotificationList`).
 *
 * Three terminal states, gated by two flags:
 *
 *   - `paired === undefined`                                 → loading
 *   - `paired === false` OR `pairingJustConfirmed === true`  → pairing
 *   - `paired === true` AND `!pairingJustConfirmed`          → main
 *
 * The `pairingJustConfirmed` branch into the pairing slot is
 * load-bearing: the extension's pairing flow flips `paired = true`
 * AND `pairingJustConfirmed = true` simultaneously, then keeps the
 * pairing screen on-screen so it can render the success checkmark
 * before the host clears the flag and the main view takes over.
 *
 * `onMainViewActive` fires once whenever the main-view branch is
 * selected — the extension uses it to dispatch MARK_READ so unread
 * counts don't carry across panel reopens. Web today has no equivalent
 * (its own MainPage handles mark-read inline) so it can omit the
 * callback. The effect intentionally re-runs whenever the predicate
 * flips back to true; that matches the legacy behaviour where every
 * reopen of a paired panel re-asserted MARK_READ.
 *
 * The default loading view is a centered `<Spinner>` inside a
 * `flex: 1` `<Box>` so the spinner fills the available vertical
 * space in flex-column parents (sidepanel). Hosts can override via
 * the `loadingView` slot if they want a different placeholder.
 */

import { type ReactNode, useEffect } from "react";
import { Box } from "../Box";
import { Spinner } from "../Spinner";
import styles from "./AppShell.module.css";

/**
 * i18n bag for visible/announced strings inside the shell.
 *
 * Today the shell only owns the loading-spinner label; the pairing
 * and main slots own their own copy. Kept as a bag (rather than a
 * single `loadingLabel` prop) so future strings can land additively
 * without breaking the public surface — same convention as the other
 * headless feature wrappers.
 */
export interface AppShellLabels {
  /** Spinner aria-label during the storage-load race. */
  loading: string;
}

export interface AppShellProps {
  /**
   * Pairing state from the host's storage adapter. Drives the three-
   * state branch alongside `pairingJustConfirmed`. `undefined` during
   * the initial storage-load race — keeps the loading spinner visible
   * so the panel doesn't flash a stale state on first render.
   */
  paired?: boolean;

  /**
   * "Pairing just confirmed" flag from storage. When true alongside
   * `paired === true`, the pairing slot stays on-screen so the success
   * checkmark renders before the host clears the flag and the main
   * view takes over. `undefined` is treated as falsey (i.e. the main
   * view renders if `paired === true`).
   */
  pairingJustConfirmed?: boolean;

  /**
   * Slot rendered when `paired === true && !pairingJustConfirmed`.
   * Required — the shell exists to gate this content on pairing
   * state, so a missing main view would always render an empty panel.
   */
  mainView: ReactNode;

  /**
   * Slot rendered when `paired === false || pairingJustConfirmed === true`.
   * (Note: `paired === undefined` is the loading branch, NOT this one
   * — `paired !== true` would conflate the two.) Required — without
   * it the unpaired branch would render nothing and silently strand
   * the user. Hosts pass their wired `<PairingView>`.
   */
  pairingView: ReactNode;

  /**
   * Slot rendered when `paired === undefined`. When omitted, falls
   * back to a centered `<Spinner>` with the `labels.loading` aria
   * label. Hosts override only when they need a different placeholder
   * (e.g. a skeleton list).
   */
  loadingView?: ReactNode;

  /**
   * Class applied to the outer `<div>` — consumers use this to set the
   * entry-specific chrome (popup width, sidepanel full height, root
   * background, etc.). Optional because stories can supply the
   * surrounding frame instead.
   */
  containerClassName?: string;

  /**
   * Fires when the main-view branch is selected (`paired === true &&
   * !pairingJustConfirmed`). Hosts use this to trigger platform-side
   * effects — the extension dispatches `MARK_READ` so unread counts
   * clear across panel reopens; the web omits the callback because
   * its own MainPage handles mark-read inline. Re-runs whenever the
   * predicate flips back to true (matches legacy behaviour).
   *
   * **Stabilise the reference** with `useCallback` (or a module-level
   * constant) — the internal `useEffect` lists `onMainViewActive` in
   * its deps so an inline `() => doThing()` from a host that re-
   * renders frequently (e.g. on every storage change) would re-fire
   * the callback on every render while the main view is active. The
   * extension wrapper passes `useCallback(() => dispatchMessage(...),
   * [])` for exactly this reason.
   */
  onMainViewActive?: () => void;

  /** i18n bag for visible/announced strings inside the shell. */
  labels: AppShellLabels;
}

export function AppShell({
  paired,
  pairingJustConfirmed,
  mainView,
  pairingView,
  loadingView,
  containerClassName,
  onMainViewActive,
  labels,
}: AppShellProps) {
  // Resolve the active branch up-front so each render sees a single
  // discriminated value instead of three cooperating booleans. The
  // three values are mutually exclusive by construction:
  //   - "loading"  → paired === undefined (storage hasn't hydrated)
  //   - "pairing"  → paired === false OR pairingJustConfirmed === true
  //                  (the second clause keeps the success animation
  //                  on-screen for a beat after `paired` flips true)
  //   - "main"     → paired === true AND NOT pairingJustConfirmed
  // A reader who eyeballs `branch === "main"` below doesn't have to
  // mentally cross-check three separate predicates to confirm
  // exclusivity.
  const branch: "loading" | "pairing" | "main" =
    paired === undefined
      ? "loading"
      : paired === false || pairingJustConfirmed === true
        ? "pairing"
        : "main";

  useEffect(() => {
    if (branch === "main") {
      onMainViewActive?.();
    }
  }, [branch, onMainViewActive]);

  return (
    <div className={containerClassName}>
      {branch === "loading" &&
        (loadingView ?? (
          <Box align="center" justify="center" paddingY="xl" className={styles.loading}>
            <Spinner size="lg" tone="brand" label={labels.loading} />
          </Box>
        ))}
      {branch === "pairing" && pairingView}
      {branch === "main" && mainView}
    </div>
  );
}
